import { useState, useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise'
import { supabase } from 'api/axios.instance'
import { getProgramList } from 'api/program/program.api'
import { fetchTutorList } from 'api/tutors/tutors.api'
import { getProgramInfoJoinTutorRPC, getToolsInventoryRpc, getToolsRentalRpc } from 'api/rpc/rpc.api'
import { getStatisticProgram } from 'api/statistics/statistics_program.api'
import { getStatisticTutor } from 'api/statistics/statistics_total_tutor.api'

import { getAllUserInfo } from 'store/Slices/user'

import Spinner from 'components/AntD/Spin'
import styles from './agGrid.module.scss'

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)])
LicenseManager.setLicenseKey(
    'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090332}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Domfam_Co.,_Ltd.}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{20_July_2026}____[v3]_[0102]_MTc4NDUwMjAwMDAwMA==f9eb0f450dab63a75694821bc1436af6',
)

const AgGrid = ({ pageType, gridType, colDefs, defaultColDef, region}) => {
    const { userInfo } = useSelector(getAllUserInfo)
    const [agGridListData, setAgGridListData] = useState([])
    const [isLoading, setLoading] = useState(true)
    const Id = userInfo.regionId === 'all' ? '' : userInfo.regionId
const regionId = region || (userInfo.regionId === 'all' ? '' : userInfo.regionId)
    const gridRef = useRef(null)

    // 숫자 값 텍스트로 처리하기 위한 스타일 정의
    const excelStyles = [
        {
            id: 'text-cell',
            dataType: 'string',
        },
    ]

    // 엑셀 다운로드 시 employment_type 변환 처리
    const defaultExportParams = {
        processCellCallback: params => {
            // employment_type 필드에서 '협동조합'을 '위촉'으로 변환
            if (params.column?.getColId() === 'employment_type' && params.value === '협동조합') {
                return '위촉'
            }
            return params.value
        },
    }
    const handleRowClick = event => {
        const tutorId = event.data.tutor_id
        const programId = event.data.program_id
        const inventoryId = event.data.tools_id
        const rentalId = event.data.rental_id
        if (rentalId) {
            window.open(`/tools/rental/${rentalId}`, '_blank', 'fullscreen=yes')
            return
        }
        if (tutorId) {
            window.open(`/tutors/${tutorId}`, '_blank', 'fullscreen=yes')
        }
        if (programId) {
            window.open(`/program/${programId}`, '_blank', 'fullscreen=yes')
        }
        if (inventoryId) {
            window.open(`/tools/inventory/${inventoryId}`, '_blank', 'fullscreen=yes')
        }
    }
    const handleFetchData = useCallback(async () => {
        try {
            setLoading(true)
            let response
            const allData = []
            let offset = 0
            const limit = 1000

            if (pageType && gridType === 'normal') {
                switch (pageType) {
                    case 'program': {
                        // eslint-disable-next-line no-constant-condition
                        while (true) {
                            // eslint-disable-next-line no-await-in-loop
                            const { data, error } = await getProgramInfoJoinTutorRPC(offset, limit, regionId)

                            if (error) {
                                console.error('Error fetching data:', error)
                                break
                            }

                            if (data) {
                                allData.push(...data)
                                if (data.length < limit) {
                                    break
                                }
                                offset += limit
                            } else {
                                break
                            }
                        }
                        break
                    }
                    case 'tutor': {
                        // RPC 함수를 사용하여 데이터 가져오기
                        // RPC 함수는 전체 데이터를 한 번에 반환하므로 한 번만 호출
                        const rpcParams = {
                            year_param: 2025,
                        }

                        // 지역 조건 추가 (null이 아닐 때만 추가)
                        const finalRegionId = region || (userInfo.regionId !== 'all' ? userInfo.regionId : null)
                        if (finalRegionId && finalRegionId !== 'all') {
                            rpcParams.region_param = finalRegionId
                        }

                        // RPC 함수 호출
                        const { data: rpcData, error: rpcError } = await supabase.rpc('get_crypted_tutor_info_list', rpcParams)

                        if (rpcError) {
                            console.error('get_crypted_tutor_info_list RPC error:', rpcError)
                            break
                        }

                        console.log('🔍 AgGrid tutor RPC 데이터:', rpcData?.length, '개')
                        console.log('🔍 AgGrid tutor RPC 파라미터:', rpcParams)

                        if (rpcData && Array.isArray(rpcData)) {
                            // 컬럼 정의가 snake_case를 사용하므로 변환하지 않고 그대로 사용
                            console.log('🔍 AgGrid tutor 데이터:', rpcData.length, '개')
                            console.log('🔍 AgGrid tutor 첫 번째 데이터 샘플:', rpcData[0])
                            allData.push(...rpcData)
                        } else {
                            console.warn('⚠️ AgGrid tutor: rpcData가 배열이 아니거나 없습니다.', rpcData)
                        }
                        break
                    }
                    case 'inventory': {
                        // eslint-disable-next-line no-constant-condition
                        while (true) {
                            // eslint-disable-next-line no-await-in-loop
                            const { data, error } = await getToolsInventoryRpc(offset, limit, Id)

                            if (error) {
                                console.error('Error fetching data:', error)
                                break
                            }

                            if (data) {
                                allData.push(...data)
                                if (data.length < limit) {
                                    break
                                }
                                offset += limit
                            } else {
                                break
                            }
                        }
                        break
                    }
                    case 'rental': {
                        // eslint-disable-next-line no-constant-condition
                        while (true) {
                            // eslint-disable-next-line no-await-in-loop
                            const { data, error } = await getToolsRentalRpc(offset, limit, Id)

                            if (error) {
                                console.error('Error fetching data:', error)
                                break
                            }

                            if (data) {
                                allData.push(...data)
                                if (data.length < limit) {
                                    break
                                }
                                offset += limit
                            } else {
                                break
                            }
                        }
                        break
                    }
                    case 'statistics_program':
                    case 'statistics_tutor': {
                        const statisticsApi =
                            pageType === 'statistics_program' ? getStatisticProgram : getStatisticTutor
                        const { data, error } = await statisticsApi(userInfo.regionId)

                        if (error) {
                            console.error(`Error fetching ${pageType} data:`, error)
                            break
                        }

                        if (data) {
                            allData.push(...data)
                        }
                        break
                    }
                    default: {
                        console.error('Unknown pageType:', pageType)
                        break
                    }
                }
                response = allData
            } else if (pageType && gridType === 'pivot') {
                let getList
                switch (pageType) {
                    case 'program':
                        getList = await getProgramList('', userInfo.regionId, 'data-table')
                        break
                    case 'tutor':
                        getList = await fetchTutorList('', userInfo.regionId, 'data-table')
                        break
                    case 'statistics_program':
                        getList = await getStatisticProgram(userInfo.regionId)
                        break
                    case 'statistics_tutor':
                        getList = await getStatisticTutor(userInfo.regionId)
                        break
                    default:
                        console.error('Unknown pageType:', pageType)
                        return
                }
                response = getList.data
            }

            if (response) {
                setAgGridListData(response)
            }
        } catch (e) {
            console.error('Unexpected error:', e)
        } finally {
            setLoading(false)
        }
    }, [pageType, userInfo.regionId, gridType, Id, region, regionId])

    useEffect(() => {
        handleFetchData()
    }, [handleFetchData])

    return (
        <div className={styles.container} style={{ height: gridType === 'pivot' ? '1000px' : 'auto' }}>
            {isLoading ? (
                <div className={styles.spin_wrap}>
                    <Spinner />
                </div>
            ) : (
                <>
                    {gridType === 'normal' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            excelStyles={excelStyles}
                            defaultExportParams={defaultExportParams}
                            domLayout="autoHeight"
                            pagination
                            cellSelection
                            rowGroupPanelShow="always"
                            enableCharts
                            onRowClicked={handleRowClick}
                            onGridReady={params => {
                                params.api.setFilterModel({
                                    registration_year: {
                                        filterType: 'set',
                                        values: ['2026'],
                                    },
                                })
                            }}
                        />
                    )}
                    {gridType === 'pivot' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            excelStyles={excelStyles}
                            defaultExportParams={defaultExportParams}
                            sideBar={['columns', 'filters']}
                            pivotPanelShow="always"
                            rowGroupPanelShow="always"
                            cellSelection
                            enableCharts
                            statusBar={{
                                statusPanels: [
                                    { statusPanel: 'agTotalRowCountComponent', align: 'left' },
                                    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                                    { statusPanel: 'agFilteredRowCountComponent' },
                                    { statusPanel: 'agSelectedRowCountComponent' },
                                    { statusPanel: 'agAggregationComponent' },
                                ],
                            }}
                        />
                    )}
                </>
            )}
        </div>
    )
}
export default AgGrid
